import { useCallback, useMemo } from 'react';
import { useEnterpriseProductsQuery } from '@mews-commander/core/entities';
import { ProductPrice, ProductPricing, ProductResult, useRepositoryQuery } from '@mews-commander/data-access';
import { useCommanderSelector } from '@mews-commander/utils';
import { useIsMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/enterprise';
import { getServiceDefaultAgeCategoryId } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { useLocalizeProperty } from '@mews/framework';
import { keyBy } from '@mews/fp';
import {
    getMaximalSelectedEndUtc,
    getMinimalSelectedStartUtc,
} from '../../../selectors/reservationModuleState/reservationSelection';
import { getProductOptions } from '../../../selectors';

export type ProductOptions = {
    value: string;
    label: React.ReactNode;
    secondaryText: React.ReactNode;
}[];

export const useGetProductOptions = ({ serviceId, scope }: { serviceId: string; scope: string }) => {
    const { data: serviceProducts, isPending: isServiceProductsPending } = useEnterpriseProductsQuery(
        useCallback(
            (result: ProductResult) =>
                result.products?.filter((product) => product.serviceId === serviceId && !product.isDeleted),
            [serviceId]
        )
    );

    const localizeProperty = useLocalizeProperty();
    const startUtc = useCommanderSelector((state) => getMinimalSelectedStartUtc(state, { scope }));
    const endUtc = useCommanderSelector((state) => getMaximalSelectedEndUtc(state, { scope }));
    const isMultiTaxEnvironment = useIsMultiTaxEnvironment();
    const defaultCategoryId = useCommanderSelector((state) => getServiceDefaultAgeCategoryId(state, { serviceId }));
    const productIds = useMemo(
        () =>
            (serviceProducts ?? [])
                .filter((product) => product.pricing?.discriminator === ProductPricing.Absolute && !product.isDefault)
                .map((product) => product.id),
        [serviceProducts]
    );

    const enableFetch =
        !isServiceProductsPending && !!startUtc && !!endUtc && !!defaultCategoryId && productIds.length > 0;

    const { data: productOptions, isLoading } = useRepositoryQuery(
        'products',
        'getPrices',
        {
            serviceId,
            startUtc: startUtc as string,
            endUtc: endUtc as string,
            consumedUtc: startUtc,
            occupancyData: [{ ageCategoryId: defaultCategoryId as string, personCount: 1 }],
            productIds,
        },
        {
            enabled: enableFetch,
            select: (data) => {
                if (!data || !data.prices) {
                    return [];
                }
                const prices = keyBy<ProductPrice, 'productId'>('productId', data.prices);
                const options = getProductOptions(isMultiTaxEnvironment, prices, localizeProperty)(serviceProducts);

                return options as ProductOptions;
            },
        }
    );

    return {
        productOptions: productOptions || [],
        isProductOptionsLoading: isLoading || isServiceProductsPending,
        serviceProducts: serviceProducts || [],
    };
};
